/**
 * klubb-predictive-search — quick results under the header search field.
 * Typing two or more characters fetches the klubb-search-suggest section
 * (Section Rendering API) with the form's own query and the visitor's scope
 * (general range + own club, via the Search & Discovery tag filter), keeps
 * the rows that are for the visitor as a fallback and shows the top six
 * with photo, name and price; Enter still submits the full search.
 * ESM module; helpers exported for tests.
 */

export const MIN_CHARS = 2;
export const SCOPE_PARAM = 'filter.p.tag';

/** Tag value that scopes a search to the visitor. Mirrors klubb-search-scope-inputs. */
export function scopeTags(club) {
  // The Search & Discovery tag filter is set to OR: general range, plus the
  // club's own products for a member.
  const slug = club && club.slug ? String(club.slug).trim().toLowerCase() : '';
  return slug ? ['generell', 'klubb-' + slug] : ['generell'];
}

/**
 * Makes sure a search form carries the scope inputs. The server only knows
 * the club on club pages; elsewhere it lives in the cookie, so the club
 * inputs are added here before the form is used.
 */
export function ensureScopeInputs(form, club) {
  const doc = form.ownerDocument;
  const wanted = scopeTags(club);
  const inputs = Array.from(form.querySelectorAll('input[name="' + SCOPE_PARAM + '"]'));
  // Liquid cannot see the cookie club, so drop scope inputs the visitor should not send.
  inputs.forEach((i) => { if (wanted.indexOf(i.value) < 0) i.remove(); });
  const present = inputs.map((i) => i.value);
  wanted.forEach((tag) => {
    if (present.indexOf(tag) > -1) return;
    const input = doc.createElement('input');
    input.type = 'hidden';
    input.name = SCOPE_PARAM;
    input.value = tag;
    input.setAttribute('data-klubb-scope-club', '');
    form.appendChild(input);
  });
}

/**
 * URL of the quick-results section, rendered by Shopify with the same
 * query and hidden fields (incl. the scope) as the header form would submit.
 */
export function suggestUrl(form, query, base, club) {
  const url = new URL(form.getAttribute('action') || '/search', base || 'https://klubb.no');
  Array.from(form.querySelectorAll('input[type="hidden"]')).forEach((i) => {
    if (i.name) url.searchParams.append(i.name, i.value);
  });
  url.searchParams.set('q', query);
  if (!url.searchParams.has('type')) url.searchParams.set('type', 'product');
  const present = url.searchParams.getAll(SCOPE_PARAM);
  scopeTags(club).forEach((tag) => { if (present.indexOf(tag) < 0) url.searchParams.append(SCOPE_PARAM, tag); });
  url.searchParams.set('section_id', 'klubb-search-suggest');
  return url.toString();
}

export function activeClub() {
  return (typeof window !== 'undefined' && window.klubb && window.klubb.activeClub) || null;
}

export const SHOWN = 6;

/**
 * Is this product for the visitor? Members see their own club's products and
 * the general range; visitors without a club see the general range only.
 * Matches klubb-product-card's crest gating (slug / short_name / initials).
 */
export function isClubProduct(li, club) {
  if (!club) return false;
  const tokens = (li.getAttribute('data-klubb-clubs') || '').split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);
  const mine = [club.slug, club.short_name, club.crest_initials].filter(Boolean).map((t) => String(t).toLowerCase());
  return tokens.some((t) => mine.indexOf(t) > -1);
}

export function productVisible(li, club) {
  return li.getAttribute('data-klubb-generell') === 'true' || isClubProduct(li, club);
}

/**
 * Puts the fetched section body into the dropdown, drops products that are
 * not for the visitor and keeps SHOWN of them, the member's own club
 * products first (Shopify ranks all clubs alike). Returns the item count.
 */
export function renderSuggestions(box, html, club) {
  const doc = box.ownerDocument;
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const body = parsed.querySelector('[data-klubb-suggest-body]');
  box.textContent = '';
  if (!body) return 0;
  Array.from(body.childNodes).forEach((n) => box.appendChild(doc.importNode(n, true)));
  const list = box.querySelector('.klubb-suggest__list');
  const rows = list ? Array.from(list.children) : [];
  const own = rows.filter((li) => isClubProduct(li, club));
  const general = rows.filter((li) => own.indexOf(li) < 0 && productVisible(li, club));
  const keep = own.concat(general).slice(0, SHOWN);
  const kept = keep.length;
  rows.forEach((li) => li.remove());
  keep.forEach((li) => list.appendChild(li));
  if (kept === 0) {
    if (list) list.remove();
    const more = box.querySelector('.klubb-suggest__more');
    if (more) more.remove();
    if (!box.querySelector('.klubb-suggest__empty')) {
      const p = doc.createElement('p');
      p.className = 'klubb-suggest__empty';
      p.textContent = box.getAttribute('data-klubb-suggest-none') || 'Ingen treff';
      box.appendChild(p);
    }
  }
  return kept;
}

export function wirePredictiveSearch(form, fetchImpl) {
  if (!form || form.dataset.klubbPredictiveWired === 'true') return;
  form.dataset.klubbPredictiveWired = 'true';
  const input = form.querySelector('input[type="search"]');
  const box = form.querySelector('[data-klubb-suggest]');
  if (!input || !box) return;
  const doFetch = fetchImpl || (typeof fetch === 'function' ? fetch.bind(window) : null);
  if (!doFetch) return;

  let timer = null;
  let controller = null;
  let lastQuery = '';

  function close() {
    box.hidden = true;
    input.setAttribute('aria-expanded', 'false');
  }
  function open() {
    box.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  function run(query) {
    if (controller) controller.abort();
    controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    doFetch(suggestUrl(form, query, window.location.href, activeClub()), { signal: controller && controller.signal, credentials: 'same-origin' })
      .then((res) => (res.ok ? res.text() : Promise.reject(new Error('HTTP ' + res.status))))
      .then((html) => {
        if (query !== lastQuery) return;
        renderSuggestions(box, html, activeClub());
        open();
      })
      .catch((err) => {
        if (err && err.name === 'AbortError') return;
        close();
      });
  }

  input.addEventListener('input', () => {
    const query = input.value.trim();
    lastQuery = query;
    clearTimeout(timer);
    if (query.length < MIN_CHARS) { close(); return; }
    timer = setTimeout(() => run(query), 180);
  });

  input.addEventListener('focus', () => {
    if (input.value.trim().length >= MIN_CHARS && box.childNodes.length) open();
  });

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') { close(); return; }
    if (event.key === 'ArrowDown' && !box.hidden) {
      const first = box.querySelector('a');
      if (first) { event.preventDefault(); first.focus(); }
    }
  });

  box.addEventListener('keydown', (event) => {
    const links = Array.from(box.querySelectorAll('a'));
    const idx = links.indexOf(document.activeElement);
    if (event.key === 'ArrowDown' && idx > -1 && idx < links.length - 1) { event.preventDefault(); links[idx + 1].focus(); }
    if (event.key === 'ArrowUp') { event.preventDefault(); if (idx > 0) links[idx - 1].focus(); else input.focus(); }
    if (event.key === 'Escape') { close(); input.focus(); }
  });

  document.addEventListener('click', (event) => {
    if (!form.contains(event.target)) close();
  });
  form.addEventListener('submit', () => {
    ensureScopeInputs(form, activeClub());
    close();
  });
}

if (typeof document !== 'undefined') {
  const init = () => document.querySelectorAll('[data-klubb-predictive]').forEach((form) => wirePredictiveSearch(form));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
}
